import { FormEvent, useEffect, useRef, useState } from "react";
import type { Asset, PlacementType, UpdateAssetSettingsRequest } from "../../types/product.types";
import {
  PLACEMENT_TYPES,
  FACE_ACCESSORY_SUBTYPES,
  HAND_ACCESSORY_SUBTYPES,
  FOOT_ACCESSORY_SUBTYPES,
} from "../../utils/constants";

interface AssetSettingsFormProps {
  asset: Asset | null;
  onSubmit: (data: UpdateAssetSettingsRequest) => Promise<void>;
  loading?: boolean;
}

type Tab = "placement" | "dimensions";

function normalizeAccessorySubtype(
  placementType: PlacementType,
  subtype: string | null | undefined,
): string {
  if (!subtype) return "";
  const normalized = subtype.toLowerCase().trim();

  if (placementType === "hand") {
    if (normalized === "clock") return "watch";
    return normalized;
  }

  if (placementType === "foot") {
    if (normalized === "sneaker") return "shoe";
    return normalized;
  }

  if (placementType === "face_accessory") {
    if (normalized === "neckless") return "necklace";
    if (normalized === "cap") return "hat";
    if (normalized === "forehead") return "bindi";
    return normalized;
  }

  return normalized;
}

export function AssetSettingsForm({ asset, onSubmit, loading = false }: AssetSettingsFormProps) {
  const [activeTab, setActiveTab] = useState<Tab>("placement");
  const [placementType, setPlacementType] = useState<PlacementType>(asset?.placement_type || "floor");
  const [accessorySubtype, setAccessorySubtype] = useState<string>(asset?.accessory_subtype || "");
  const [scaleX, setScaleX] = useState(asset ? (asset.scale_x * 100).toString() : "100");
  const [scaleY, setScaleY] = useState(asset ? (asset.scale_y * 100).toString() : "100");
  const [scaleZ, setScaleZ] = useState(asset ? (asset.scale_z * 100).toString() : "100");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialisedForId = useRef<string | null>(null);
  const lastServerSubtype = useRef<string>("");
  const lastServerPlacement = useRef<PlacementType | null>(null);

  useEffect(() => {
    if (!asset) return;
    const isNewAsset = asset.id !== initialisedForId.current;
    const normalizedSubtype = normalizeAccessorySubtype(asset.placement_type, asset.accessory_subtype);
    const serverSubtypeChanged = normalizedSubtype !== lastServerSubtype.current;
    const serverPlacementChanged = asset.placement_type !== lastServerPlacement.current;
    lastServerSubtype.current = normalizedSubtype;
    lastServerPlacement.current = asset.placement_type;

    if (isNewAsset) {
      initialisedForId.current = asset.id;
      setPlacementType(asset.placement_type);
      setScaleX((asset.scale_x * 100).toString());
      setScaleY((asset.scale_y * 100).toString());
      setScaleZ((asset.scale_z * 100).toString());
      setAccessorySubtype(normalizedSubtype);
      return;
    }
    // Sync from server only when values actually changed remotely to avoid UI flip-flop while editing.
    if (!isSubmitting && serverPlacementChanged) {
      setPlacementType(asset.placement_type);
    }
    if (!isSubmitting && serverSubtypeChanged) {
      setAccessorySubtype(normalizedSubtype);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, asset?.accessory_subtype, asset?.placement_type, isSubmitting]);

  const handlePlacementChange = (value: PlacementType) => {
    setPlacementType(value);
    setAccessorySubtype("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const payload: UpdateAssetSettingsRequest = {
        placement_type: placementType,
        scale_x: parseFloat(scaleX) / 100,
        scale_y: parseFloat(scaleY) / 100,
        scale_z: parseFloat(scaleZ) / 100,
      };
      const normalizedSubtype = normalizeAccessorySubtype(placementType, accessorySubtype);
      if (normalizedSubtype) {
        payload.accessory_subtype = normalizedSubtype;
      }
      await onSubmit(payload);
    } catch (error) {
      console.error("Failed to update settings:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabled = isSubmitting || loading;

  const subtypeOptions =
    placementType === "face_accessory"
      ? FACE_ACCESSORY_SUBTYPES
      : placementType === "hand"
      ? HAND_ACCESSORY_SUBTYPES
      : placementType === "foot"
      ? FOOT_ACCESSORY_SUBTYPES
      : null;

  const tabs: { id: Tab; label: string }[] = [
    { id: "placement", label: "Placement" },
    { id: "dimensions", label: "Dimensions" },
  ];

  return (
    <form onSubmit={handleSubmit} className="glass-panel space-y-4">
      {/* Tab navigation */}
      <div className="flex gap-1 rounded-2xl bg-slate-100 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition ${
              activeTab === tab.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Placement tab */}
      {activeTab === "placement" && (
        <div className="space-y-5">
          <div>
            <p className="text-sm font-semibold text-slate-900">Placement type</p>
            <p className="text-xs text-slate-500">Choose how the model anchors inside AR.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {PLACEMENT_TYPES.map(({ label, value }) => (
                <label
                  key={value}
                  className={`cursor-pointer rounded-2xl border px-3 py-2 text-sm font-semibold transition ${
                    placementType === value
                      ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="placementType"
                    value={value}
                    checked={placementType === value}
                    onChange={() => handlePlacementChange(value)}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {subtypeOptions && (
            <div>
              <p className="text-sm font-semibold text-slate-900">Accessory type</p>
              <p className="text-xs text-slate-500 mb-2">
                Determines the AR anchor point and fit.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {subtypeOptions.map(({ label, value, hint }) => (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-2.5 transition ${
                      accessorySubtype === value
                        ? "border-indigo-400 bg-indigo-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="accessorySubtype"
                      value={value}
                      checked={accessorySubtype === value}
                      onChange={() => setAccessorySubtype(value)}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <span>
                      <span
                        className={`block text-sm font-semibold ${
                          accessorySubtype === value ? "text-indigo-700" : "text-slate-700"
                        }`}
                      >
                        {label}
                      </span>
                      <span className="block text-xs text-slate-400">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dimensions tab */}
      {activeTab === "dimensions" && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Real-world dimensions</p>
            <p className="text-xs text-slate-500">
              Enter the actual product size in centimetres (width × height × depth). Values are stored as
              metres in the database and passed to the storefront AR widget for true-to-scale placement (same as
              AR config world dimensions when no JSON override is set).
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { label: "Width (cm)", value: scaleX, setter: setScaleX },
              { label: "Height (cm)", value: scaleY, setter: setScaleY },
              { label: "Depth (cm)", value: scaleZ, setter: setScaleZ },
            ].map((field) => (
              <label key={field.label} className="text-sm text-slate-500">
                <span className="mb-2 block font-semibold text-slate-700">{field.label}</span>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={field.value}
                  onChange={(e) => field.setter(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={disabled}
        className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}
